<template lang="pug" src="./driver-editor.pug"></template>
<style lang="scss" src="./driver-editor.scss" scoped></style>

<script>
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, minLength, maxLength, minValue, email, numeric } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';
import { demical } from '@/utils/helpers';

import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        driverDetails: {
            commissionId: null,
            commissionSystemId: null,
            callSign: null,
            minBalance: 10,
            address: null,
            additionalPhoneNumbers: null,
            additionalAdresses: null,
            pledge: null,
            passportNumber: null,
            passportIssuedBy: null,
            passportIssuedWhen: null,
            identificationCode: null,
            reRegistration: null,
            comments: [],
            additionalDriver: null,
            mainDriver: null,
            leaderId: null,
            applicationCode: null,
            deliveryConfidence: 0
        },
        user: {
            firstName: null,
            lastName: null,
            middleName: null,
            phoneNumber: null,
            comment: null,
            email: null,
            brandId: null
        },
        car: {
            color: null,
            model: null,
            make: null,
            number: null,
            year: null,
            options: []
        },
        payments: {
            filters: {
                startDate: new Date().addDays(-7).format('{yyyy}-{MM}-{dd}'),
                endDate: new Date().addDays(1).format('{yyyy}-{MM}-{dd}'),
                startTime: '00:00',
                endTime: '23:59',
                conditions: [],
                paymentTypeIds: []
            },
            items: [],
            sum: 0,
            balance: 0,
        },
        payment: {
            paymentSum: '',
            paymentComment: '',
            paymentType: null,
            orderId: null
        },
        activeOrderPayment: {
            orderId: null,
            paymentId: null
        },
        canConfirmCommission: false,
        recalculateCommission: {
            distance: null,
            downtime: null,
            commissionAmount: null,
            totalOrderPrice: null
        },
        penalty: {
            penaltyId: '',
            penaltyComment: '',
            orderId: null
        },
        comment: {
            text: null
        },
        tariffsIds: [],
        optionsIds: [],
        driverPenalties: [],
        reRegistrationModal: false,
        removeModal: false,
        recalculateCommissionModal: false,
        canSaveDriver: false,
        loaded: false,
        startDateModal: false,
        endDateModal: false,
        startTimeModal: false,
        endTimeModal: false,
        pagination: {
            page: 1,
            totalPages: 1
        },
        requestProcessing: false,
        dataLoading: false,
        passwordSending: false,
        driverStatusChange: false,
        leadersList: []
    }),
    validations: {
        driverDetails: {
            callSign: {
                required
            },
            minBalance: {
                required,
                numeric,
                minValue: minValue(10)
            },
            commissionId: {
                required
            },
            address: {
                required
            },
            passportNumber: {
                required
            },
            passportIssuedBy: {
                required
            },
            passportIssuedWhen: {
                required
            },
            identificationCode: {
                required
            }
        },
        car: {
            year: {
                required,
                numeric
            },
            model: {
                required
            },
            color: {
                required
            },
            make: {
                required
            },
            number: {
                required
            }
        },
        user: {
            firstName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            lastName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            middleName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            phoneNumber: {
                required
            },
            email: {
                required,
                email
            },
            comment: {
                maxLength: maxLength(255)
            },
            brandId: {
                required
            }
        },
        penalty: {
            penaltyId: {
                required
            },
            penaltyComment: {
                required
            }
        },
        payment: {
            paymentSum: {
                required,
                numeric: demical
            },
            paymentComment: {
                required
            },
            paymentType: {
                required
            },
            orderId: {
                custom: function(value) {
                    return !(!value && this.displayOrderRequired);
                }
            }
        },
        recalculateCommission: {
            distance: {
                required,
                minValue: value => value >= 0
            },
            downtime: {
                required,
                minValue: value => value >= 0
            }
        }
    },
    computed: {
        ...mapGetters(['roleName', 'userBrandId', 'operator']),
        ...mapState({
            brands: state => state.data.brands,
            deliveryConfidenceLevels: state => state.data.deliveryConfidenceLevels,
            commissions: state => state.data.driverCommissions,
            commissionSystems: state => state.data.driverCommissionSystems,
            brandOptions: state => state.data.brandOptions,
            paymentTypes: state => state.paymentTypes.list
        }),
        saveMethod() {
            return this.user.id ? 'put' : 'post';
        },
        saveUrl() {
            const userId = this.user.id;

            return userId ? `/drivers/${userId}` : '/drivers';
        },
        options() {
            if (!this.user.brandId) {
                return [];
            }

            return this.brandOptions && this.brandOptions[this.user.brandId]
                && this.brandOptions[this.user.brandId].options || [];
        },
        tariffs() {
            if (!this.user.brandId) {
                return [];
            }

            return this.brandOptions && this.brandOptions[this.user.brandId]
                && this.brandOptions[this.user.brandId].tariffs || [];
        },
        canDisabled: function() {
            return this.isMainRole && ['active', 'offline', 'online'].includes(this.driverDetails.driverStatus);
        },
        canActive: function() {
            return this.isMainRole && ['disabled', 'removed'].includes(this.driverDetails.driverStatus);
        },
        canPayment: function() {
            return this.isMainRolePlus;
        },
        canPenalty: function() {
            return this.isMainRole;
        },
        canEdit: function() {
            return ['admin'].includes(this.roleName) ||
                ['boss'].includes(this.roleName) && this.operator.canEditDrivers;
        },
        canResetIMEI: function() {
            return this.isMainRole && this.driverDetails.imei != null;
        },
        canResetAppCode: function() {
            return this.isMainRole && this.driverDetails.applicationCode != null;
        },
        canSendPassword: function() {
            return this.isMainRole && this.user.phoneNumber && this.driverDetails.callSign != null;
        },
        isMainRolePlus() {
            return ['accountant', 'manager', 'admin', 'boss'].includes(this.roleName);
        },
        isMainRole() {
            return ['manager', 'admin', 'boss'].includes(this.roleName);
        },
        canRecalculateCommission() {
            return ['admin', 'manager'].includes(this.roleName);
        },
        selectAllOptions() {
            return this.optionsIds.length === this.options.length;
        },
        selectSomeOptions() {
            return this.optionsIds.length > 0 && !this.selectAllOptions;
        },
        selectAllTariffs() {
            return this.tariffsIds.length === this.tariffs.length;
        },
        selectSomeTariffs() {
            return this.tariffsIds.length > 0 && !this.selectAllTariffs;
        },
        headers() {
            return ['createdAt', 'orderId', 'fullNameForPayment', 'paymentComment', 'paymentSum', 'paymentType'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Users.payments.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        isRemoved() {
            return this.driverDetails.driverStatus && this.driverDetails.driverStatus === 'removed';
        },
        changeDriverStatusText() {
            return this.canActive ? 'enable' : 'disable';
        },
        validComment() {
            return this.comment.text && this.comment.text.length <= 255;
        },
        paymentTypesByComment() {
            return [
                'manual-replenishment', 'cashless-sprut', 'cashless-manual',
                'cash-out', 'receipt', 'employee', 'mistake', 'transfer-plus',
                'transfer-minus', 'penalty', 'return'
            ].map(it => ({
                value: it,
                text: this.$vuetify.lang.t(`$vuetify.pages.Users.payments.types.${it}`)
            }));
        },
        penaltyPaymentType() {
            return this.paymentTypes.find({ name: 'penalty' }).id;
        },
        paymentTypesForCreating() {
            return this.paymentTypes.filter((type) => type.hidden === false);
        },
        displayOrderRequired() {
            return this.payment.paymentType && this.payment.paymentType.orderRequired;
        },
        mappedDeliveryConfidenceLevels() {
            return this.deliveryConfidenceLevels.map((item, i) => ({value: i, text: this.formatDeliveryConfidence(item)}));
        }
    },
    watch: {
        driverDetails: {
            handler() {
                this.canSaveDriver = true;
            },
            deep: true
        },
        user: {
            handler() {
                this.canSaveDriver = true;
            },
            deep: true
        },
        car: {
            handler() {
                this.canSaveDriver = true;
            },
            deep: true
        },
        optionsIds() {
            this.canSaveDriver = true;
        },
        tariffsIds() {
            this.canSaveDriver = true;
        }
    },
    created: async function() {
        this.$store.dispatch('data/getBrands');
        this.$store.dispatch('data/getOptions');
        this.$store.dispatch('data/getConfidenceLevels');
        this.$store.dispatch('paymentTypes/getItems', {
            fetchAll: true
        });

        this.getLeadersList();

        if (this.userBrandId) {
            this.user.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;
            await this.loadData();
        }

        this.loadDriverCommissions();
        this.loadDriverCommissionSystems();
    },
    methods: {
        formatDeliveryConfidence(item) {
            if (item.max) {
                return `${this.$vuetify.lang.t('$vuetify.pages.Users.driver.confidenceLevels.under')} ${item.max}${this.$vuetify.lang.t('$vuetify.hrn')}`;
            } else {
              return this.$vuetify.lang.t('$vuetify.pages.Users.driver.confidenceLevels.all');
            }
        },
        async loadData() {
            await request({ method: 'get', url: `/drivers/${this.$route.params.id}` })
                .then(res => this.successLoad(res))
                .catch(error => {
                    const { status, data } = error.response;
                    const message = data && data.error;

                    if (status === 409) {
                        this.$router.push('/users/drivers');
                    }

                    if (message) {
                        this.$store.dispatch('alert/addMessage', {
                            message
                        });
                    }
                }).finally(() => {
                    this.loaded = false;
                });
        },

        async getLeadersList() {
            await request({ method: 'get', url: '/leaders' })
                .then(res => {
                    if (res.data) {
                        this.leadersList = res.data;
                    }
                }).catch(error => this.handleError(error));
        },

        loadDriverCommissions() {
            const payload = {};

            if (this.user.brandId) {
                payload.brandIds = JSON.stringify([this.user.brandId]);
            }

            this.$store.dispatch('data/getDriverCommissions', payload);
        },

        loadDriverCommissionSystems() {
            const payload = {};

            if (this.user.brandId) {
                payload.brandId = this.user.brandId;
            }

            this.$store.dispatch('data/getDriverCommissionSystems', payload);
        },

        onAutocompleteChange: function(key, value) {
            this.canSaveDriver = true;

            if (key == 'penaltyId') {
                return this.$set(this.penalty, key, value);
            }

            this.$set(this.driverDetails, key, value);
        },

        async saveDriver() {
            if (this.$v.user.$invalid || this.$v.driverDetails.$invalid || this.$v.car.$invalid) {
                this.$v.driverDetails.$touch();
                this.$v.car.$touch();
                this.$v.user.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, {
                user: this.user,
                driverDetails: Object.assign({}, this.driverDetails, {
                    commissionSystemId: this.driverDetails.commissionSystemId || null,
                    leaderId: this.driverDetails.leaderId || null
                }),
                car: Object.assign({}, this.car, {
                    options: [...this.tariffsIds, ...this.optionsIds]
                })
            });

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    this.$v.driverDetails.$reset();
                    this.$v.car.$reset();
                    this.$v.user.$reset();
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        async loadDriverPayments(page = 1) {
            if (this.dataLoading) return;

            this.dataLoading = true;

            await request(`/drivers/payments/${this.user.id}`, {
                params: Object.assign({}, this.payments.filters, {
                    page,
                    conditions: JSON.stringify(this.payments.filters.conditions),
                    paymentTypeIds: JSON.stringify(this.payments.filters.paymentTypeIds)
                })
            }).then(res => {
                if (res.data) {
                    this.payments.items = res.data.payments;
                    this.payments.sum = res.data.sum;
                    this.payments.balance = res.data.balance;

                    this.pagination = {
                        page: res.data.page,
                        totalPages: res.data.totalPages
                    };
                }
            }).finally(() => {
                this.$nextTick(() => this.dataLoading = false);
            });
        },

        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.loadDriverPayments(page);
            }
        },

        async loadDriverPenalties(title, forSelect = false) {
            const params = {
                brandId: this.user.brandId,
                ignorePaginate: true
            };

            if (forSelect) {
                Object.assign(params, {
                    title
                });
            }

            await request('/admin/driver/penalties', { params }).then(res => {
                if (res.data) {
                    this.driverPenalties = res.data.items;
                }
            });
        },

        async addDriverPenalty() {
            if (this.$v.penalty.$invalid) {
                this.$v.penalty.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const penalty = this.driverPenalties.find(it => it.id == this.penalty.penaltyId);
            const text = this.$vuetify.lang.t('$vuetify.pages.Users.payments.commissionText');

            const data = {
                penaltyId: penalty.id,
                paymentComment: `${text} ${penalty.title} - ${this.penalty.penaltyComment}`,
                paymentSum: -1 * parseInt(penalty.fixedPenalty),
                paymentTypeId: this.penaltyPaymentType,
                orderId: this.penalty.orderId
            };

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/payment`,
                data
            }).then(() => {
                this.loadDriverPayments();
                this.$store.dispatch('alert/addMessage', {
                    type: 'success',
                    message: 'success'
                });

                this.penalty = {
                    penaltyId: '',
                    penaltyComment: '',
                    orderId: null
                };
                this.$v.penalty.$reset();
            }).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    const message = error.response.data.error;

                    this.$store.dispatch('alert/addMessage', {
                        message: this.$vuetify.lang.t(`$vuetify.messages.error.payments.${message}`),
                        locale: false
                    });
                }
            }).finally(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                });
            });
        },

        async addDriverPayment() {
            if (this.$v.payment.$invalid) {
                this.$v.payment.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.payment, {
                paymentTypeId: this.payment.paymentType && this.payment.paymentType.id
            });

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/payment`,
                data
            }).then(() => {
                this.loadDriverPayments();
                this.$store.dispatch('alert/addMessage', {
                    type: 'success',
                    message: 'success'
                });

                this.payment = {
                    paymentSum: null,
                    paymentComment: null,
                    orderId: null
                };
                this.$v.payment.$reset();
            }).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    const message = error.response.data.error;

                    this.$store.dispatch('alert/addMessage', {
                        message: this.$vuetify.lang.t(`$vuetify.messages.error.payments.${message}`),
                        locale: false
                    });
                }
            }).finally(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                });
            });
        },

        openRecalculateModal({ orderId, id: paymentId, commissionId }) {
            if (!this.canRecalculateCommission || !(commissionId && orderId)) {
                return;
            }

            this.activeOrderPayment = Object.assign({}, {
                orderId, paymentId
            });

            this.recalculateCommissionModal = true;
        },

        closeRecalculateModal() {
            Object.keys(this.recalculateCommission).forEach(it => {
                this.recalculateCommission[it] = null;
            });

            this.$v.recalculateCommission.$reset();
            this.recalculateCommissionModal = false;
            this.canConfirmCommission = false;
        },

        async recalculateDriverCommission() {
            if (this.$v.recalculateCommission.$invalid) {
                this.$v.recalculateCommission.$touch();
                return;
            }

            const { orderId } = this.activeOrderPayment;
            await request({
                method: 'post',
                url: `/orders/${orderId}/recalculate-commission`,
                data: { ...this.recalculateCommission }
            }).then(res => {
                if (res.data) {
                    this.recalculateCommission = Object.assign({}, {
                        ...this.recalculateCommission,
                        ...Object.select(res.data, ['commissionAmount', 'totalOrderPrice'])
                    });

                    this.canConfirmCommission = true;
                }
            }).catch(err => this.handleError(err));
        },

        async confirmCommission() {
            const { orderId, paymentId } = this.activeOrderPayment;

            await request({
                method: 'put',
                url: `/orders/${orderId}/confirm-commission`,
                data: { ...this.recalculateCommission }
            }).then(res => {
                if (res.status === 204) {
                    const activePaymentIndex = this.payments.items.findIndex(it => it.id === paymentId);

                    if (activePaymentIndex !== -1) {
                        this.$set(this.payments.items[activePaymentIndex], 'paymentSum', -this.recalculateCommission.commissionAmount);

                        this.$store.dispatch('alert/addMessage', {
                            type: 'success',
                            message: 'success'
                        });

                        this.closeRecalculateModal();
                    }
                }
            }).catch(err => this.handleError(err));
        },

        handleError(err) {
            const message = err.response && err.response.data && err.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    type: 'error',
                    message
                });
            }
        },

        async changeDriverStatus() {
            if (this.driverStatusChange) return;

            this.driverStatusChange = true;

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/change-status`
            }).then(res => {
                this.successLoad(res);
                this.$store.dispatch('alert/addMessage', {
                    type: 'success',
                    message: 'success'
                });
            }).catch(err => {
                this.handleError(err);
            }).finally(() => {
                this.$nextTick(() => {
                    this.driverStatusChange = false;
                });
            });
        },

        async resetBinding(type) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const assoc = {
                'code': 'reset-code',
                'phone': 'reset-phone'
            };

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/${assoc[type]}`
            }).then(res => {
                this.successLoad(res);
                this.$store.dispatch('alert/addMessage', {
                    type: 'success',
                    message: 'success'
                });
            }).finally(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                });
            });
        },

        async sendPasswordAgain() {
            if (this.passwordSending) return;

            this.passwordSending = true;

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/password`
            }).then(res => {
                this.successLoad(res);

                this.$v.driverDetails.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
            }).finally(() => {
                this.$nextTick(() => {
                    this.passwordSending = false;
                });
            });
        },

        changeBrand() {
            this.optionsIds = [];
            this.tariffsIds = [];

            this.driverDetails.commissionSystemId = null;
            this.driverDetails.commissionId = null;

            this.loadDriverCommissions();
            this.loadDriverCommissionSystems();
            this.loadDriverPenalties();
        },

        successLoad(res) {
            const { data } = res;

            this.driverDetails = Object.assign({}, {
                ...Object.select(data, ['id', 'driverStatus', 'imei', ...Object.keys(this.driverDetails)])
            }, {
                reRegistration: data.reRegistration && new Date(data.reRegistration).format('{yyyy}-{MM}-{dd}') || null
            });

            ['user', 'car'].forEach(key => {
                this[key] = Object.assign({}, Object.select(data[key], [
                    'id', ...Object.keys(this[key])
                ]));
            });

            this.optionsIds = data.car.carOptions.filter(it => !it.defaultOptions).map('id');
            this.tariffsIds = data.car.carOptions.filter(it => it.defaultOptions).map('id');

            if (this.user.id && !this.$route.params.id) {
                this.$router.push(`/drivers/editor/${this.user.id}`);
            }

            this.loadDriverPayments();
            this.loadDriverPenalties();

            if (data.password) {
                const passwordText = this.$vuetify.lang.t('$vuetify.pages.Users.driver.changePasswordText');

                this.$store.dispatch('alert/addMessage', {
                    type: 'success',
                    message: `${passwordText} ${data.password}`,
                    locale: false,
                    time: 20 * 1000
                });
            }

            this.$nextTick(() => {
                this.canSaveDriver = false;
            });
        },

        async remove() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/drivers/${this.user.id}` })
                .then(res => {
                    if(res.status == 200) {
                        this.$router.push('/users/drivers');
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.removeModal = false;
                    });
                });
        },

        getIcon(selectAll, selectSome) {
            if (selectAll) {
                return 'mdi-close-box';
            }

            if (selectSome) {
                return 'mdi-minus-box';
            }

            return 'mdi-checkbox-blank-outline';
        },

        toggleTariffs() {
            this.$nextTick(() => {
                if (this.selectAllTariffs) {
                    this.tariffsIds = [];
                } else {
                    this.tariffsIds = this.tariffs.map('id');
                }
            });
        },

        toggleOptions() {
            this.$nextTick(() => {
                if (this.selectAllOptions) {
                    this.optionsIds = [];
                } else {
                    this.optionsIds = this.options.map('id');
                }
            });
        },

        saveByRef(ref) {
            this.$refs[ref].save(this.payments.filters[ref]);
            this.loadDriverPayments();
        },

        resetData(pickerType) {
            const action = {
                startDate: this.resetStartDate,
                endDate: this.resetEndDate
            };

            action[pickerType]();

            this.loadItems();
        },

        resetStartDate() {
            this.startDateModal = false;
            this.filters.startDate = new Date().addDays(-1).format('{yyyy}-{MM}-{dd}');
        },

        resetEndDate() {
            this.endDateModal = false;
            this.filters.endDate = new Date().addDays(1).format('{yyyy}-{MM}-{dd}');
        },

        getChipColorByStatus(status) {
            return ['disabled', 'removed'].includes(status) ? 'error' : 'success';
        },

        async createAdditionalDriver() {
            if (this.$v.user.$invalid || this.$v.driverDetails.$invalid || this.$v.car.$invalid) {
                this.$v.driverDetails.$touch();
                this.$v.car.$touch();
                this.$v.user.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/additional`,
            }).then(res => {
                if (res.status == 200) {
                    this.$set(this.driverDetails, 'additionalDriver', Object.assign({}, res.data.additionalDriver, {
                        driverStatus: 'disabled'
                    }));
                }
            }).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    this.$store.dispatch('alert/addMessage', {
                        message: error.response.data.error
                    });
                }
            }).finally(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                });
            });
        },

        async createNewComment() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({
                method: 'post',
                url: `/drivers/${this.user.id}/comment`,
                data: this.comment
            })
                .then(res => {
                    if (res.status == 200) {
                        this.driverDetails.comments.unshift(res.data);

                        this.comment.text = null;
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        async destroyComment(index, commentId) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({
                method: 'delete',
                url: `/drivers/${this.user.id}/${commentId}/comment`
            })
                .then(res => {
                    if (res.status == 204) {
                        this.driverDetails.comments.splice(index, 1);
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        }
    }
};
</script>
